/**
 * MyStatsWidget - Виджет "Мои показатели"
 *
 * Показывает статистику сейла за период:
 * - Звонки
 * - Встречи
 * - Мастер-классы
 * - Договоры
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Stack,
  Group,
  Text,
  Title,
  Box,
  Loader,
  Center,
  SegmentedControl,
  RingProgress,
  ThemeIcon,
} from '@mantine/core';
import {
  IconChartBar,
  IconPhone,
  IconUsers,
  IconSchool,
  IconFileCheck,
} from '@tabler/icons-react';
import tokens from '../../theme/designTokens';

const METRIC_CONFIG = {
  calls: {
    icon: IconPhone,
    color: 'blue',
    label: 'Звонки',
  },
  meetings: {
    icon: IconUsers,
    color: 'green',
    label: 'Встречи',
  },
  master_classes: {
    icon: IconSchool,
    color: 'orange',
    label: 'МК',
  },
  contracts: {
    icon: IconFileCheck,
    color: 'violet',
    label: 'Договоры',
  },
};

const MetricCard = ({ metricKey, count, target }) => {
  const config = METRIC_CONFIG[metricKey];
  const Icon = config.icon;
  const progress = target > 0 ? Math.min((count / target) * 100, 100) : 0;
  const isCompleted = count >= target;

  return (
    <Group gap="sm" p="sm" style={{
      backgroundColor: tokens.colors.neutral[1],
      borderRadius: '12px',
    }}>
      <RingProgress
        size={50}
        thickness={5}
        roundCaps
        sections={[{ value: progress, color: isCompleted ? 'green' : config.color }]}
        label={
          <ThemeIcon size="sm" radius="xl" color={isCompleted ? 'green' : config.color} variant="light">
            <Icon size={14} />
          </ThemeIcon>
        }
      />
      <Box style={{ flex: 1 }}>
        <Text size="xs" c="dimmed">
          {config.label}
        </Text>
        <Text size="lg" fw={700}>
          {count}
          <Text span size="xs" c="dimmed" fw={400}> / {target}</Text>
        </Text>
      </Box>
    </Group>
  );
};

const MyStatsWidget = () => {
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/dashboard/sales/stats?period=${period}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Общий прогресс
  const totalProgress = data ? (() => {
    const total = data.calls.target + data.meetings.target + data.master_classes.target + data.contracts.target;
    const completed = data.calls.count + data.meetings.count + data.master_classes.count + data.contracts.count;
    return total > 0 ? (completed / total) * 100 : 0;
  })() : 0;

  return (
    <Card shadow="sm" radius={16} withBorder p="lg" h="100%">
      <Stack gap="md">
        {/* Заголовок */}
        <Group justify="space-between">
          <Group gap="xs">
            <IconChartBar size={20} style={{ color: tokens.colors.brand.primary }} />
            <Title order={4}>Мои показатели</Title>
          </Group>
        </Group>

        {/* Переключатель периода */}
        <SegmentedControl
          value={period}
          onChange={setPeriod}
          size="xs"
          data={[
            { value: 'week', label: 'Неделя' },
            { value: 'month', label: 'Месяц' },
            { value: 'quarter', label: 'Квартал' },
          ]}
        />

        {/* Метрики */}
        {loading ? (
          <Center py="xl">
            <Loader size="sm" />
          </Center>
        ) : data ? (
          <Stack gap="xs">
            <MetricCard
              metricKey="calls"
              count={data.calls.count}
              target={data.calls.target}
            />
            <MetricCard
              metricKey="meetings"
              count={data.meetings.count}
              target={data.meetings.target}
            />
            <MetricCard
              metricKey="master_classes"
              count={data.master_classes.count}
              target={data.master_classes.target}
            />
            <MetricCard
              metricKey="contracts"
              count={data.contracts.count}
              target={data.contracts.target}
            />
          </Stack>
        ) : (
          <Center py="xl">
            <Text size="sm" c="dimmed">Нет данных</Text>
          </Center>
        )}

        {/* Общий прогресс */}
        {data && (
          <Box
            p="sm"
            style={{
              backgroundColor: tokens.colors.brand.light,
              borderRadius: '12px',
            }}
          >
            <Group justify="space-between">
              <Text size="sm" fw={500}>
                Общий прогресс
              </Text>
              <Text size="sm" fw={700} style={{ color: tokens.colors.brand.primary }}>
                {Math.round(totalProgress)}%
              </Text>
            </Group>
          </Box>
        )}
      </Stack>
    </Card>
  );
};

export default MyStatsWidget;
